import { t } from 'ember-intl';
import { getSingleValueForTasField } from 'tio-common/utils/tuition-assistance/fields';
import Button from 'tio-ui/components/button';
import type { RadioOption } from 'tio-ui/components/radio-group';
import { tracked } from '@glimmer/tracking';
import { dropTask } from 'ember-concurrency';
import Component from '@glimmer/component';
import Svg from 'tio-employee/components/svg';
import { divide } from 'tio-ui/math';
import safeFormatNumber from 'tio-common/helpers/safe-format-number';
import type { TasApplicationExtension as TasApplicationModel } from 'tio-data/data/resources/tas-application.ext';
import { service } from '@ember/service';
import { saveRecord } from 'tio-data/requests/save-record';
import type { Store as StoreService } from '@warp-drive/core';
import type RouterService from '@ember/routing/router-service';
import type IntlService from 'ember-intl/services/intl';
import type FeaturesService from 'tio-common/services/features';
import { applicationDetailRoute } from 'tio-employee/utils/tas/manage-link';
import LpnSurveyCard from 'tio-employee/components/tas/applications/lpn-survey-card';
import { Section, VStack } from 'tio-ui/components/layout';
import type { TOC } from '@ember/component/template-only';
import { or } from 'tio-ui/utilities';
import type { TasCourseExtension as TASCourseModel } from 'tio-data/data/resources/tas-course.ext';

export interface S {
  Args: {
    model: {
      tasType: string;
      application: TasApplicationModel;
      evidence: string;
      showLpnSurvey: boolean;
    };
  };
}

export default class AuthenticatedTasConfirmation extends Component<S> {
  @service declare router: RouterService;
  @service declare intl: IntlService;
  @service declare features: FeaturesService;
  @service declare store: StoreService;

  @tracked selectedLpnOption?: string;
  @tracked lpnSurveySubmitted = false;

  get isTypeTwo() {
    const { tasType } = this.args.model;
    return tasType === 'two';
  }

  get isTypeThree() {
    const { tasType } = this.args.model;
    return tasType === 'three';
  }

  get isTypeFour() {
    const { tasType } = this.args.model;
    return tasType === 'four';
  }

  get isEvidenceSubmission() {
    const { evidence } = this.args.model;
    return evidence ? JSON.parse(evidence) === true : false;
  }

  get applicationName() {
    return getSingleValueForTasField(
      'APPLICATION_NAME',
      this.args.model.application.fields
    ) as string;
  }

  get isDependentProgram() {
    return this.args.model.application.tasProgramInstance.isDependentProgram;
  }

  get lpnSurveyOptions(): RadioOption[] {
    return [
      { key: 'yes', label: this.intl.t('tuition_assistance.submitted.lpn_survey.got_discount') },
      { key: 'no', label: this.intl.t('tuition_assistance.submitted.lpn_survey.no_discount') },
    ];
  }

  setLpnOption = (value: string) => {
    this.selectedLpnOption = value;
  };

  submitLpnSurveyTask = dropTask(async () => {
    const { application } = this.args.model;
    application.lpnDiscountActivated = this.selectedLpnOption === 'yes';
    await saveRecord(this.store, application);
    this.lpnSurveySubmitted = true;
  });

  goToDashboard = () => {
    if (this.isDependentProgram) {
      this.router.transitionTo('authenticated.tas.dependent-programs');
    } else {
      this.router.transitionTo('authenticated.tas.dashboard');
    }
  };

  goToNewApplication = () => {
    const { id } = this.args.model.application.tasProgramInstance.tasProgramTemplate;
    this.router.transitionTo('authenticated.tas.getting-started', id);
  };

  goToEvidenceSubmission = () => {
    const { application } = this.args.model;
    const route = applicationDetailRoute(
      application.typeClassification,
      this.features.isEnabled('tasCourseManagement')
    );
    this.router.transitionTo(route, application.id);
  };

  <template>
    <VStack>
      <Section>
        <:body>
          {{#if this.isTypeTwo}}
            <SubmissionBlock
              @type="two"
              @isEvidenceSubmission={{false}}
              @application={{@model.application}}
              @applicationName={{this.applicationName}}
              @goToDashboard={{this.goToDashboard}}
              @goToEvidenceSubmission={{this.goToNewApplication}}
              @showLpnSurvey={{@model.showLpnSurvey}}
              @lpnSurveyOptions={{this.lpnSurveyOptions}}
              @selectedLpnOption={{this.selectedLpnOption}}
              @onSelectLpnOption={{this.setLpnOption}}
              @onSubmitLpnSurvey={{this.submitLpnSurveyTask.perform}}
              @isSubmittingLpnSurvey={{this.submitLpnSurveyTask.isRunning}}
              @lpnSurveySubmitted={{this.lpnSurveySubmitted}}
            />
          {{/if}}

          {{#if this.isTypeThree}}
            <SubmissionBlock
              @type="three"
              @isEvidenceSubmission={{this.isEvidenceSubmission}}
              @application={{@model.application}}
              @applicationName={{this.applicationName}}
              @goToDashboard={{this.goToDashboard}}
              @goToEvidenceSubmission={{this.goToEvidenceSubmission}}
              @showLpnSurvey={{@model.showLpnSurvey}}
              @lpnSurveyOptions={{this.lpnSurveyOptions}}
              @selectedLpnOption={{this.selectedLpnOption}}
              @onSelectLpnOption={{this.setLpnOption}}
              @onSubmitLpnSurvey={{this.submitLpnSurveyTask.perform}}
              @isSubmittingLpnSurvey={{this.submitLpnSurveyTask.isRunning}}
              @lpnSurveySubmitted={{this.lpnSurveySubmitted}}
            />
          {{/if}}

          {{#if this.isTypeFour}}
            <SubmissionBlock
              @type="four"
              @isEvidenceSubmission={{this.isEvidenceSubmission}}
              @application={{@model.application}}
              @applicationName={{this.applicationName}}
              @goToDashboard={{this.goToDashboard}}
              @goToEvidenceSubmission={{this.goToEvidenceSubmission}}
              @showLpnSurvey={{@model.showLpnSurvey}}
              @lpnSurveyOptions={{this.lpnSurveyOptions}}
              @selectedLpnOption={{this.selectedLpnOption}}
              @onSelectLpnOption={{this.setLpnOption}}
              @onSubmitLpnSurvey={{this.submitLpnSurveyTask.perform}}
              @isSubmittingLpnSurvey={{this.submitLpnSurveyTask.isRunning}}
              @lpnSurveySubmitted={{this.lpnSurveySubmitted}}
            />
          {{/if}}
        </:body>
      </Section>
    </VStack>
  </template>
}

const courseName = (course: TASCourseModel) => {
  return getSingleValueForTasField('COURSE_NAME', course.fields) as string;
};

const courseNumber = (course: TASCourseModel) => {
  return getSingleValueForTasField('COURSE_NUMBER', course.fields) as string;
};

interface SubmissionBlockSigniture {
  Args: {
    isEvidenceSubmission: boolean;
    type: string;
    applicationName: string;
    application: TasApplicationModel;
    goToDashboard: () => void;
    goToEvidenceSubmission: () => void;
    showLpnSurvey: boolean;
    lpnSurveyOptions: RadioOption[];
    selectedLpnOption: string | undefined;
    onSelectLpnOption: (value: string) => void;
    onSubmitLpnSurvey: () => void;
    isSubmittingLpnSurvey: boolean;
    lpnSurveySubmitted: boolean;
  };
}
const SubmissionBlock: TOC<SubmissionBlockSigniture> = <template>
  <div class="tio-stack-center w-full mt-8 p-8 md:p-0">
    <div class="flex mb-4">
      <Svg @name="mail-sent" width="212px" role="img" aria-label={{t "svg.mail_sent"}} />
    </div>
    <div>
      <h1 class="text-4xl mb-4 text-center">
        {{#if @isEvidenceSubmission}}
          {{t "tuition_assistance.submitted.evidence_submitted"}}
        {{else}}
          {{t "tuition_assistance.submitted.application_submitted"}}
        {{/if}}
      </h1>
      <h3 class="font-semibold mb-4 text-center md:text-xl md:leading-9">
        {{t "tuition_assistance.submitted.what_happens_now"}}
      </h3>
    </div>

    {{#if @isEvidenceSubmission}}
      {{! Evidence Submission List }}
      <ul class="list-disc pl-4 leading-9 md:text-xl md:leading-9">
        <li>{{t "tuition_assistance.submitted.your_evidence_will_be_reviewed"}}</li>
        <li>{{t "tuition_assistance.submitted.you_can_review_your_application"}}</li>
      </ul>
    {{else}}
      {{! Application Submission List }}
      <ul class="list-disc pl-4 leading-9 md:text-xl md:leading-9">
        <li>
          {{t "tuition_assistance.submitted.your_application_name"}}
          <span class="font-semibold">&nbsp;{{@applicationName}}</span>
        </li>

        {{#if @application.activeCourses.length}}
          <li>{{t "tuition_assistance.submitted.courses_in_your_app"}}:</li>
          <ul class="list-inside list-disc">
            {{#each @application.activeCourses as |course|}}
              <li class="font-semibold">
                <span>{{courseName course}} {{courseNumber course}}</span>
              </li>
            {{/each}}
          </ul>
        {{/if}}

        {{#if @application.scholarships.length}}
          <li>{{t "tuition_assistance.submitted.scholarships_in_your_app"}}:</li>
          <ul class="list-inside list-disc">
            {{#each @application.scholarships as |award|}}
              <li class="font-semibold">
                {{award.scholarshipName}}
                -
                {{safeFormatNumber
                  (divide award.scholarshipAmount 100)
                  style="currency"
                  currency="USD"
                }}
              </li>
            {{/each}}
          </ul>
        {{/if}}

        <li>{{t "tuition_assistance.submitted.your_application_will_be_reviewed"}}</li>
        {{#if (or (eq @type "three") (eq @type "four"))}}
          <li>{{t "tuition_assistance.submitted.once_your_application_is_accepted"}}</li>
        {{/if}}
        <li>{{t "tuition_assistance.submitted.you_can_review_your_application"}}</li>
      </ul>
    {{/if}}

    {{#if @showLpnSurvey}}
      <LpnSurveyCard
        @options={{@lpnSurveyOptions}}
        @selected={{@selectedLpnOption}}
        @onSelect={{@onSelectLpnOption}}
        @onSubmit={{@onSubmitLpnSurvey}}
        @isSubmitting={{@isSubmittingLpnSurvey}}
        @submitted={{@lpnSurveySubmitted}}
      />
    {{/if}}

    <div class="mt-6">
      <Button @intent="primary" {{on "click" @goToDashboard}}>
        {{t "tuition_assistance.submitted.go_to_dashboard"}}
      </Button>

      {{#if (eq @type "two")}}
        <Button @intent="primary" @appearance="outlined" {{on "click" @goToEvidenceSubmission}}>
          {{t "tuition_assistance.submitted.submit_another_application"}}
        </Button>
      {{else if @isEvidenceSubmission}}
        <Button @intent="primary" @appearance="outlined" {{on "click" @goToEvidenceSubmission}}>
          {{t "tuition_assistance.view_details"}}
        </Button>
      {{else}}
        <Button @intent="primary" @appearance="outlined" {{on "click" @goToEvidenceSubmission}}>
          {{t "common.progress_tracker.view_application"}}
        </Button>
      {{/if}}
    </div>
  </div>
</template>;
